import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

/**
 * QRCodeDisplay Component
 * 
 * Generates and displays a QR code for a display
 */

interface QRCodeDisplayProps {
  displayId: string;
  displayName: string;
  displayUrl?: string;
  size?: number;
  colorDark?: string;
  colorLight?: string;
  quality?: number;
  includeMargin?: boolean;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  displayId,
  displayName,
  displayUrl,
  size = 200,
  colorDark = '#000000',
  colorLight = '#ffffff',
  quality = 0.92,
  includeMargin = true
}) => {
  const [qrUrl, setQrUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Generate the QR code when the component mounts or props change
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Determine the URL to encode
        const urlToEncode = displayUrl || 
          `${window.location.origin}/display/view/${displayId}`;
        
        // Generate QR code as data URL
        const url = await QRCode.toDataURL(urlToEncode, {
          width: size,
          margin: includeMargin ? 4 : 0,
          color: {
            dark: colorDark,
            light: colorLight
          },
        });
        
        setQrUrl(url);
      } catch (err) {
        console.error('Error generating QR code:', err);
        setError(err instanceof Error ? err.message : 'Failed to generate QR code');
      } finally {
        setLoading(false);
      }
    };

    generateQRCode();
  }, [displayId, displayUrl, size, colorDark, colorLight, includeMargin]);

  // Handle download of the QR code
  const handleDownload = () => {
    if (!qrUrl) return;
    
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `${displayName.replace(/\s+/g, '-').toLowerCase()}-qrcode.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="text-center p-3">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Generating QR Code...</span>
        </div>
        <span className="ms-2">Generating QR Code...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="qr-code-display text-center">
      <div className="mb-3">
        <img 
          src={qrUrl} 
          alt={`QR Code for ${displayName}`} 
          className="img-thumbnail"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      </div>
      <div className="d-grid gap-2">
        <button 
          className="btn btn-primary" 
          onClick={handleDownload}
        >
          Download QR Code
        </button>
      </div>
      <div className="mt-2 text-muted small">
        <p>Scan this QR code to view the display or share with others.</p>
      </div>
    </div>
  );
};

export default QRCodeDisplay; 